/**
 * An in-app confirmation for something destructive.
 *
 * NOT `window.confirm`. Three reasons, and only the first is about testing:
 *
 *  1. A browser modal cannot be styled, so it cannot meet the legibility
 *     standard — it renders at whatever size the OS feels like, which on a dim
 *     machine room screen is the whole problem D-011 exists to solve.
 *  2. It is a synchronous dialog that blocks the event loop, which the harness
 *     rules forbid outright, and it is invisible to the DOM so nothing can
 *     assert on what it said.
 *  3. Its buttons say "OK" and "Cancel". The whole point of a confirmation is
 *     that the words describe the ACT — "Delete from this phone" versus
 *     "Withdraw from the office" — and a native dialog cannot carry them.
 *
 * The panel names what is being acted on in its title (the caller supplies it,
 * and every caller in this build includes the survey's own name), explains the
 * consequence at full helper size ABOVE the buttons, and puts the cancel first
 * in reading order so the safe choice is the one under the thumb.
 */

import { useId, type ReactNode } from 'react';

export interface ConfirmPanelProps {
  /** Names what is being acted on. "Delete “St Luke's” from this phone?" */
  title: string;
  /** What will happen. Full helper size, above the buttons that do it. */
  body: string;
  /** The destructive button's words. Never "OK". */
  confirmLabel: string;
  cancelLabel: string;
  /** True while the act is in flight; both buttons go inert. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmPanel({
  title,
  body,
  confirmLabel,
  cancelLabel,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmPanelProps): ReactNode {
  const titleId = useId();
  const bodyId = useId();

  return (
    <section
      className="confirm"
      data-testid="confirm-panel"
      /* `alertdialog` is the role for a confirmation that interrupts to ask
       * about a destructive act. It is not modal — it is laid into the page
       * under the control that opened it, so nothing is hidden behind it and
       * there is no focus trap to escape from. */
      role="alertdialog"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      <p className="q" id={titleId} data-legibility="question">
        {title}
      </p>
      {/* Above the buttons, at full size and weight: standard item 6. */}
      <p className="leadin" id={bodyId} data-legibility="helper">
        {body}
      </p>
      <div className="rowbtns">
        <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
          {cancelLabel}
        </button>
        <button type="button" className="btn btn--danger" disabled={busy} onClick={onConfirm}>
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </section>
  );
}
